import React from "react";
import { Pill } from "./Ui";
import { Button } from "./ui/button";

const STATUS_META = {
  SYNCED: { tone: "success", label: "Synced", dot: "🟢" },
  RE_SYNC_REQUIRED: { tone: "warning", label: "Re-Sync Required", dot: "🟡" },
  FAILED: { tone: "danger", label: "Sync Failed", dot: "🔴" },
  PENDING: { tone: "info", label: "Sync Pending", dot: "🔵" },
  NOT_SYNCED: { tone: "muted", label: "Not Synced", dot: "⚪" },
  ALREADY_POSTED_MANUAL: { tone: "muted", label: "Already posted (manual)", dot: "⚪" },
};

export function tallyStatusMeta(status) {
  return STATUS_META[status] || STATUS_META.NOT_SYNCED;
}

/**
 * Tally sync + ERP approval badge.
 *
 * Props:
 * - status: tally_sync_status
 * - approvalStatus: sync_approval_status
 * - approvedBy / approvedAt / rejectionReason
 * - canApprove: show Approve & Sync / Reject
 * - onApprove / onReject
 * - busy
 */
export default function TallySyncBadge({
  status,
  error,
  className = "",
  showDot = true,
  approvalStatus,
  approvedBy,
  approvedAt,
  rejectionReason,
  canApprove = false,
  onApprove,
  onReject,
  busy = false,
}) {
  const approval = String(approvalStatus || "").toUpperCase();
  const sync = String(status || "NOT_SYNCED").toUpperCase();
  const meta = tallyStatusMeta(sync);

  if (approval === "PENDING_APPROVAL" && canApprove) {
    return (
      <span className={`inline-flex flex-col items-start gap-1.5 ${className}`}>
        <Pill tone="warning">Pending ERP approval</Pill>
        <span className="flex flex-wrap gap-1.5">
          <Button
            type="button"
            size="sm"
            className="h-7 bg-emerald-700 hover:bg-emerald-800 text-white"
            disabled={busy}
            onClick={() => onApprove?.()}
          >
            Approve &amp; Sync to Tally
          </Button>
          <Button
            type="button"
            size="sm"
            variant="destructive"
            className="h-7"
            disabled={busy}
            onClick={() => onReject?.()}
          >
            Reject
          </Button>
        </span>
      </span>
    );
  }

  if (approval === "PENDING_APPROVAL") {
    return (
      <span className={`inline-flex flex-col items-start gap-0.5 ${className}`}>
        <Pill tone="warning">Pending ERP approval</Pill>
      </span>
    );
  }

  if (approval === "REJECTED") {
    return (
      <span
        className={`inline-flex flex-col items-start gap-0.5 ${className}`}
        title={rejectionReason || "Rejected"}
      >
        <Pill tone="danger">Rejected</Pill>
        {rejectionReason ? (
          <span className="text-[10px] text-red-700 max-w-[16rem] truncate normal-case tracking-normal">
            {rejectionReason}
          </span>
        ) : null}
      </span>
    );
  }

  if (approval === "APPROVED" && sync === "SYNCED") {
    return (
      <span className={`inline-flex flex-col items-start gap-0.5 ${className}`}>
        <Pill tone="success">
          {showDot ? <span aria-hidden>🟢</span> : null}
          Synced to Tally
        </Pill>
        {approvedBy ? (
          <span className="text-[10px] text-muted-foreground max-w-[16rem] truncate normal-case tracking-normal">
            by {approvedBy}
            {approvedAt ? ` · ${String(approvedAt).slice(0, 16).replace("T", " ")}` : ""}
          </span>
        ) : null}
      </span>
    );
  }

  if (approval === "APPROVED") {
    return (
      <span className={`inline-flex flex-col items-start gap-0.5 ${className}`} title={error || meta.label}>
        <Pill tone={meta.tone}>
          {showDot ? <span aria-hidden>{meta.dot}</span> : null}
          Approved · {meta.label}
        </Pill>
        {error && sync === "FAILED" ? (
          <span className="text-[10px] text-red-700 max-w-[16rem] truncate normal-case tracking-normal">
            {error}
          </span>
        ) : null}
      </span>
    );
  }

  if (approval === "LEGACY_UNRESTRICTED" && sync === "ALREADY_POSTED_MANUAL") {
    return (
      <span className={`inline-flex flex-col items-start gap-0.5 ${className}`}>
        <Pill tone="muted">Already posted in Tally</Pill>
      </span>
    );
  }

  return (
    <span className={`inline-flex flex-col items-start gap-0.5 ${className}`} title={error || meta.label}>
      <Pill tone={meta.tone}>
        {showDot ? <span aria-hidden>{meta.dot}</span> : null}
        {meta.label}
      </Pill>
      {sync === "FAILED" && error ? (
        <span className="text-[10px] text-red-700 max-w-[16rem] truncate normal-case tracking-normal">
          {error}
        </span>
      ) : null}
    </span>
  );
}
